import { useId } from 'react'
import { match, P } from 'ts-pattern'
import type { FeedbackScope } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { SegmentedControl } from '../input/segmented-control.tsx'
import * as styles from './feedback-scope-selector.css.ts'

/** Why Project is unavailable: the one reason, shared by the help line and the option's description. */
export const UNGROUPED_REASON = "This repository isn't in a project yet."

export type FeedbackScopeSelectorProps = {
  value: FeedbackScope
  onChange: (value: FeedbackScope) => unknown
  /**
   * The repository the result belongs to; its name anchors the repository-level reach. Omitted until
   * the repository's membership arrives (or when it cannot be read): Project stays disabled and no
   * reach is stated, since there is nothing yet to state it about.
   */
  repositoryName?: string
  /** The repository's project, when it has one. Without it the project level cannot be chosen. */
  project?: { name: string; repositoryCount?: number }
  /**
   * Set once a submission came back refused because the repository is in no project — membership
   * changed under the open modal. Project stays dead until a fresh read shows the repository grouped.
   */
  isProjectRefused?: boolean
}

/**
 * Where a piece of feedback applies: the repository's project, or the one repository (the default,
 * always). The help line spells out the blast radius of the current choice.
 */
export const FeedbackScopeSelector: React.FC<FeedbackScopeSelectorProps> = ({
  value,
  onChange,
  repositoryName,
  project,
  isProjectRefused = false,
}) => {
  const eyebrowId = useId()
  const isMembershipKnown = repositoryName !== undefined
  const isProjectUngrouped = isMembershipKnown && (project === undefined || isProjectRefused)
  const isProjectDisabled = !isMembershipKnown || isProjectUngrouped
  const reach = match({ value, project, isProjectDisabled })
    .with({ value: 'project', project: P.nonNullable, isProjectDisabled: false }, ({ project: named }) => (
      <>
        the{' '}
        {named.repositoryCount === undefined ? (
          <b className={styles.helpSubject}>repositories</b>
        ) : (
          <b className={styles.helpSubject}>
            {named.repositoryCount} {pluralize(named.repositoryCount, 'repository', 'repositories')}
          </b>
        )}{' '}
        in <b className={styles.helpSubject}>{named.name}</b>
      </>
    ))
    .otherwise(() => (
      <>
        <b className={styles.helpSubject}>{repositoryName}</b> only
      </>
    ))

  return (
    <div className={styles.container}>
      <p className={styles.eyebrow} id={eyebrowId}>
        This feedback applies to
      </p>
      <SegmentedControl<FeedbackScope>
        ariaLabelledBy={eyebrowId}
        // Project cannot be what is sent while it is disabled, so it cannot be what is pressed either.
        value={isProjectDisabled ? 'repository' : value}
        onChange={onChange}
        options={[
          {
            value: 'project',
            label: 'Project',
            disabled: isProjectDisabled,
            disabledReason: isProjectUngrouped ? UNGROUPED_REASON : undefined,
          },
          { value: 'repository', label: 'Repository' },
        ]}
      />
      {isMembershipKnown && (
        <p className={styles.help}>
          Applies to {reach}.{isProjectUngrouped && ` ${UNGROUPED_REASON}`}
        </p>
      )}
    </div>
  )
}
